import { clearAuthStorage } from './authSession';
import { track } from './analytics';
import { deriveApiEvent, normalizeApiPath } from './analyticsRouteMap';

export function getApiBaseUrl(): string {
  const w = window as any;

  // Optional runtime injection (best for Render/prod without rebuild)
  const injected =
    typeof w.__API_BASE_URL__ === "string" ? w.__API_BASE_URL__.trim() : "";
  if (injected) return injected.replace(/\/+$/, "");

  // Build-time override (CRA env var) — useful when the FE/BE split is fixed at build time.
  const fromEnv =
    typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL
      ? String(process.env.REACT_APP_API_BASE_URL).trim()
      : "";
  if (fromEnv) return fromEnv.replace(/\/+$/, "");

  const origin = window.location.origin;

  // ✅ Local dev: any localhost port (CRA auto-bumps to 3001/3002/3003 if 3000 is taken).
  // The backend listens on PORT from .env (defaults to 3001 for this repo), so always
  // route the API to localhost:3001 in dev.
  const isLocalhost =
    /^https?:\/\/localhost(:\d+)?$/i.test(origin) ||
    /^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin);
  if (isLocalhost) return "http://localhost:5000";

  // ✅ If you host FE+BE on different domains and forget injection,
  // this would be wrong. So keep same-origin as default, but rely on injection for split deployments.
  return origin.replace(/\/+$/, "");
}

// ✅ Keep /api routing consistent with your backend mounting
export const API_BASE_URL = `${getApiBaseUrl()}/api`;

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const lower = String(text || "").toLowerCase();
    const isAuthStatus = res.status === 401 || res.status === 403;
    const isTokenExpired =
      lower.includes("token expired") ||
      lower.includes("jwt expired") ||
      lower.includes("log in again") ||
      lower.includes("login again");

    if (isAuthStatus && isTokenExpired) {
      try {
        clearAuthStorage();
        const detail = text ? { message: text } : {};
        window.dispatchEvent(new CustomEvent("session-expired", { detail }));
      } catch (_) {}
    }

    // The backend's error responses are JSON ({ success, message, errors, ... }); throwing the
    // raw response text put the whole JSON blob (including internal driver errors like a Mongo
    // E11000 string) straight into whatever alert/toast displays err.message. Extract just the
    // message field for err.message, and fall back to the raw text only when the body doesn't
    // parse as JSON (e.g. an HTML error page from a proxy/gateway).
    let message = text || `Request failed with status ${res.status}`;
    let body: any = null;
    try {
      const parsed = JSON.parse(text);
      body = parsed;
      if (parsed && typeof parsed.message === "string" && parsed.message.trim()) {
        message = parsed.message;
      }
    } catch (_) {}

    const err = new Error(message) as Error & { body?: any; status?: number };
    // Callers that need structured details (field-level validation errors, etc.) beyond the
    // top-level message should read err.body instead of re-parsing err.message.
    err.body = body;
    err.status = res.status;
    throw err;
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }

  const text = await res.text().catch(() => "");
  return text as unknown as T;
}

function logFetchHintOnce(err: unknown) {
  try {
    const origin = window.location.origin || "";
    const isLocal =
      /localhost:3000$/i.test(origin) || /127\.0\.0\.1:3000$/i.test(origin);
    if (!isLocal) return;

    const key = "__api_fetch_hint_logged__";
    if ((window as any)[key]) return;
    (window as any)[key] = true;

    // eslint-disable-next-line no-console
    console.warn(
      "[apiClient] Fetch failed. Check backend http://localhost:3001, CORS, and API_BASE_URL.",
      err
    );
  } catch (_) {}
}

/** Shown when the device itself has no connectivity. */
export const OFFLINE_MESSAGE =
  "No internet connection — please check your network.";
/** Shown when we're online but the request still never reached the server. */
export const NETWORK_ERROR_MESSAGE =
  "Can't reach the server — please check your connection and try again.";

/**
 * `fetch` rejects with a TypeError ("Failed to fetch" / "NetworkError when
 * attempting to fetch resource", wording differs per browser) when the request
 * never reached the server at all — offline, DNS failure, CORS, server down.
 * Every other failure (any HTTP status) resolves and is handled by
 * handleResponse, so this is the ONE case to translate. Doing it here means
 * every lib/*Api.ts module gets the friendly text without touching each page.
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/**
 * `fetch`, but a connectivity failure rejects with a readable message instead of
 * the browser's raw "Failed to fetch". Resolves the Response completely
 * untouched, so it is a drop-in replacement for `fetch` at any call site that
 * does its own `response.ok` / `response.json()` handling — which is why
 * services/api.ts can adopt it without a single behaviour change (it keeps its
 * own 401 redirect, its validationErrors flags, and its per-call messages).
 *
 * This is the single choke point: doFetch below uses it too, so there is one
 * implementation of "what does the user see when the network is down".
 */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    logFetchHintOnce(err);

    if (isNetworkError(err)) {
      // navigator.onLine is only trustworthy when it says false (true just means
      // "a network interface exists", not that the internet works), so use it to
      // pick the more specific wording and fall back to the generic one.
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      const friendly = new Error(
        offline ? OFFLINE_MESSAGE : NETWORK_ERROR_MESSAGE
      ) as Error & { isNetworkError?: boolean; cause?: unknown };
      friendly.isNetworkError = true;
      friendly.cause = err;
      throw friendly;
    }

    throw err;
  }
}

/** Requests slower than this emit an api_latency event (we do NOT abort — quote
 *  generation + file extraction legitimately run for minutes). */
const SLOW_API_MS = 4000;

async function doFetch<T>(
  url: string,
  init: RequestInit
): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const endpoint = normalizeApiPath(url);
  const startedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  try {
    const res = await apiFetch(url, {
      ...init,
      // ✅ safe even if you don't use cookies now; required if cookies are ever used
      credentials: "include",
    });
    const latencyMs = Math.round(
      (typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt
    );

    // API health — recorded before handleResponse throws on a non-2xx status.
    if (!res.ok) {
      track("api_error", { endpoint, method, status: res.status });
    } else if (latencyMs > SLOW_API_MS) {
      track("api_latency", { endpoint, method, latency_ms: latencyMs });
    }

    const out = await handleResponse<T>(res); // throws on non-2xx

    // Successful mutation -> auto business event (Tier-1 whole-system coverage).
    const ev = deriveApiEvent(method, endpoint);
    if (ev) track(ev.name, ev.params);

    return out;
  } catch (err: any) {
    // Connectivity failure (apiFetch tagged it) -> record as an API error too.
    if (err && err.isNetworkError) {
      track("api_error", { endpoint, method, status: 0, error_type: "network" });
    }
    throw err;
  }
}

export async function get<T>(path: string) {
  const token = localStorage.getItem("token");
  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };
  return doFetch<T>(`${API_BASE_URL}${path}`, { method: "GET", headers });
}

export async function patch<T>(path: string, body: unknown) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

export async function put<T>(path: string, body: unknown) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

export async function postJson<T>(path: string, body: unknown) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

export async function postFormData<T>(path: string, formData: FormData) {
  const token = localStorage.getItem("token");
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers, // ✅ DO NOT set Content-Type for FormData
    body: formData,
  });
}

export async function deleteRequest<T>(path: string) {
  return doFetch<T>(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
}
